import {
  animate,
  query,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ContainerVm, ResourceVm } from 'qp-ui-interface';
import { takeUntil } from 'rxjs/operators';
import { DataService } from '../data.service';
import { LanguageService } from '../language.service';
import { Destroyable } from '../shared/services/destroyable';
import { MetaDataService } from '../shared/services/meta-data.service';
import { NavigationLibraryService } from '../shared/services/navigation/navigation.service';
import { RouteHistoryService } from '../shared/services/router-history.service';
import { FocusRestorationService } from '../shared/services/utility/focus-restoration.service';

@Component({
  selector: 'app-collection',
  templateUrl: './collection.component.html',
  styleUrls: ['./collection.component.scss'],

  animations: [
    trigger('zoomStagger', [
      transition(':enter', [
        query(
          '.zoom-item',
          [
            style({ transform: 'scale(0.8)', opacity: 0 }),
            stagger(100, [
              animate(
                '300ms ease-out',
                style({ transform: 'scale(1)', opacity: 1 })
              ),
            ]),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class CollectionComponent
  extends Destroyable
  implements OnInit, OnDestroy
{
  saved = false; //flag used by reuse rout
  isPaginating = false;

  container: ContainerVm;
  resources: ResourceVm[] = null;
  resource: ResourceVm = null;
  private pageNumber: number = 1;
  private readonly pageSize: number = 20;
  private lang: string = 'en';

  constructor(
    private route: ActivatedRoute,
    private readonly dataService: DataService,
    private readonly navService: NavigationLibraryService,
    private readonly metaDataService: MetaDataService,
    private readonly routeHistory: RouteHistoryService,
    private readonly focusRestorationService: FocusRestorationService,
    private readonly languageService: LanguageService
  ) {
    super();
  }

  ngOnInit(): void {
    this.cleanup();
    this.dataService.emitVerticalMenuEvent({
      action: 'hide',
    });

    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      const collections = data['collection'] as ContainerVm[];
      if (!collections || collections?.length < 1) {
        this.goBack();
      } else if (!this.resources) {
        this.container = collections[0];
        this.resources = this.container.resources;
      }
    });
    this.setupSubscription();
    this.lang = this.languageService.currLang;
  }

  setupSubscription() {
    this.dataService.currentResource
      .pipe(takeUntil(this.destroy$))
      .subscribe((resource) => {
        this.resource = resource;
      });
  }

  ngAfterViewInit() {
    this.navService.focusContainer('items-wrapper', false, 0, true);
  }

  ngOnDestroy() {
    this.cleanup();
    this.dataService.emitVerticalMenuEvent({
      action: 'show',
    });
  }

  goBack() {
    this.pageNumber = 1;
    this.dataService.shouldSkipResolver = true;
    this.routeHistory.goBack();
  }

  get header() {
    return this.container.name[this.lang];
  }

  async onArrowDown(event: KeyboardEvent) {
    if (event.isDown()) {
      await this.paginate();
    }
  }

  async paginate(): Promise<void> {
    if (this.isPaginating) {
      return;
    }
    const totalResources = this.container.contentCount;
    if (this.resources?.length < totalResources) {
      this.isPaginating = true;
      const result = await this.metaDataService.fetchViewMore(
        this.container.viewMoreUrl,
        ++this.pageNumber,
        this.pageSize,
        this.container
      );
      this.resources = [...this.resources, ...result.resources];
      this.isPaginating = false;
    }
  }

  reload() {
    this.setupSubscription();
    // This method will be called by the reuse strategy when the component is reused
    this.focusRestorationService.restoreGridFocus({
      containerSelector: '.collection-container',
    });
    this.dataService.emitVerticalMenuEvent({
      action: 'hide',
    });
  }

  stop() {
    this.cleanup();
  }

  shouldSave() {
    return this.resources && this.resources?.length > 0;
  }
}
